import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BOTTOM_MENU, MAIN_MENU } from "@/constants/nav";
import { createRipple } from "@/utils/ripple";
import { cx } from "@/utils/cx";



const NavItem = ({ 
  item, 
  isExpanded, 
  isActive, 
  onClick,
}: { 
  item: any, 
  isExpanded: boolean, 
  isActive?: boolean, 
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void,
}) => {
  const Icon = item.icon;

  return (
    <div
      onClick={onClick}
      className="group flex items-center cursor-pointer h-[60px] w-full"
    >
      {/* Icon Circle Container */}
      <div className="w-[100px] shrink-0 flex items-center justify-center">
        <div
          className={`
            w-[52px] h-[52px] rounded-full flex items-center justify-center transition-all duration-300
            ${isActive ? "bg-brand shadow-lg scale-105" : "group-hover:bg-brand/10"}
          `}
        >
          <Icon
            className={`
              w-6 h-6 transition-colors duration-200
              ${isActive ? "text-white"  : "text-gray-500 group-hover:text-brand"}
            `}
          />
        </div>
      </div>

      {/* Label Text */}
      <div className={cx("overflow-hidden flex items-center h-full")}>
        <span
          className={`
            whitespace-nowrap text-lg font-medium transition-all duration-300 ease-in-out
            ${isActive ? "text-brand" : "text-gray-90"}
            ${isExpanded 
              ? "opacity-100 translate-x-0 delay-100" 
              : "opacity-0 -translate-x-4 pointer-events-none"
            }
          `}
        >
          {item.label}
        </span>
      </div>
    </div>
  );
};

export const Sidebar = () => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const navItems = [...MAIN_MENU, ...BOTTOM_MENU];

  const [activeItem, setActiveItem] = React.useState<string>(() => {
        const currentPath = window.location.pathname;
        const matched = navItems
            .filter(item => currentPath.startsWith(item.path))
            .sort((a, b) => b.path.length - a.path.length)[0];

        return matched ? matched.id : "dashboard";
    });

    useEffect(() => {
        const currentPath = location.pathname;
        const matched = navItems
            .filter(item => currentPath.startsWith(item.path))
            .sort((a, b) => b.path.length - a.path.length)[0];

        setActiveItem(matched ? matched.id : "dashboard");
    }, [location.pathname]);

    const handleNavClick = (e: React.MouseEvent<HTMLDivElement>, path: string) => {
        createRipple(e);
        navigate(path);
    };
    
  return (
    <div className="relative shrink-0 w-[100px] h-[calc(100vh-86px)]">
      <aside
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          fixed left-0 h-[calc(100vh-86px)] border-r border-gray-20 
          flex flex-col items-start justify-between py-4 z-40 overflow-y-auto overflow-x-hidden
          transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
          ${isHovered 
            ? "w-[260px] bg-white shadow-2xl" 
            : "w-[100px] bg-white" 
          }
        `}
      >
        {/* Main Section */}
        <div className="flex flex-col w-full">
          {MAIN_MENU.map((item, index) => (
            <NavItem
              key={item.id}
              item={item}
              isExpanded={isHovered}
              isActive={activeItem === item.id}
              onClick={(e) => handleNavClick(e, item.path)}
            />
          ))}
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col w-full mb-4">
          {BOTTOM_MENU.map((item, index) => (
            <NavItem
              key={item.id}
              item={item}
              isExpanded={isHovered}
              isActive={activeItem === item.id}
              onClick={(e) => handleNavClick(e, item.path)}
            />
          ))}
        </div>
      </aside>
    </div>
  );
};